import api from "./client";

const BASE = "/emotion-settings";

/**
 * Fetch emotion settings grouped by scoped and creator
 * @param {Object} filters - Optional query filters
 * @param {boolean} [filters.active]
 * @param {string} [filters.scope]
 * @param {string} [filters.entityId]
 * @param {string} [filters.createdBy]
 * @returns {Promise<{byScope: Object, byCreator: Object}>}
 */
export const getEmotionSettings = async () => {
  try {
    const { data } = await api.get(BASE);

    return data;
  } catch {
    throw new Error("Failed to fetch emotion settings");
  }
};

/**
 * Create one or more emotion settings
 * @param {Array<{
 *    emotion: string,
 *    threshold_percentage: number;
 *    threshold_count: number,
 *    days: number,
 *    tier: 2|3,
 *    scope: string,
 *    entityId?: string | string[]
 * }>} settings
 * @returns {Promise<{result: Array}>}
 */
export const createEmotionSettings = async (settings) => {
  if (!Array.isArray(settings) || settings.length === 0) {
    throw new Error("Settings must a non-empty array");
  }

  const filteredSettings = settings.map((setting) => {
    return {
      emotion: setting.emotion,
      threshold_percentage: setting.threshold_percentage,
      threshold_count: setting.threshold_count,
      days: setting.days,
      tier: setting.tier,
      scope: setting.scope,
      entityId: setting.entityId,
    };
  });

  try {
    const { data } = await api.post(BASE, filteredSettings);
    return data;
  } catch (e) {
    throw new Error("Failed to create emotion settings");
  }
};

/**
 * Update a single emotion setting by ID
 * @param {string} id - Setting _id
 * @param {Partial<{
 *    emotion: string,
 *    threshold_percentage: number,
 *    threshold_count: number,
 *    days: number,
 *    tier: 2|3,
 *    scope: string,
 *    entityId?: string | string[]
 * }>} updates
 * @returns {Promise<{result: Object}>}
 */
export const updateEmotionSetting = async (id, updates) => {
  const { data } = await api.patch(`${BASE}/${id}`, updates);

  return data;
};

/**
 * Delete a single emotion setting by ID
 * @param {string} id
 * @returns {Promise<{result: {_id: string}}>}
 */
export const deleteEmotionSetting = async (id) => {
  const { data } = await api.delete(`${BASE}/${id}`);
  return data;
};
